import { ApiError } from '@/shared/api/client';
import type { ApiErrorBody } from './types';

/**
 * O que a tela precisa saber de uma falha da API de marketing.
 *
 * - `unavailable`: a rota não existe no servidor (404 sem envelope JSON, 501+).
 *   Em produção é o módulo ainda não deployado/ligado no fixe-api.
 * - `not_contracted`: 403 `code: not_contracted` (flag do canal desligada na loja).
 * - `forbidden`: 403 por papel/loja (inclui o BFF barrando o `x-passbook-id`).
 * - `not_found`: 404 com mensagem da API (campanha, template ou loja).
 * - `conflict`: 409 (estado mudou: "já não é rascunho", "atualize a página").
 * - `rejected`: 422 com `blocks` (as frases de "Antes de aprovar").
 * - `network`: o fetch nem chegou a responder.
 */
export type MarketingErrorKind =
  | 'unavailable'
  | 'not_contracted'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'rejected'
  | 'network'
  | 'unknown';

export interface MarketingError {
  kind: MarketingErrorKind;
  status: number;
  message: string;
  /** Frases do bloqueio (422) ou vazio. */
  blocks: string[];
  code: string | null;
}

export const MODULE_UNAVAILABLE_MESSAGE =
  'O módulo de e-mail e SMS marketing ainda não está ligado no servidor.';

function readBody(data: unknown): { message: string; code: string | null; blocks: string[]; envelope: boolean } {
  const body = (data && typeof data === 'object' ? data : {}) as ApiErrorBody;
  const error = body.error && typeof body.error === 'object' ? body.error : null;
  const message =
    (error && typeof error.message === 'string' && error.message.trim()) ||
    (typeof body.message === 'string' && body.message.trim()) ||
    '';
  const code = error && typeof error.code === 'string' ? error.code : null;
  const blocks =
    error && Array.isArray(error.blocks)
      ? error.blocks.filter((item): item is string => typeof item === 'string' && item.trim() !== '')
      : [];
  return { message, code, blocks, envelope: error !== null };
}

export function toMarketingError(error: unknown, fallback = 'Não foi possível concluir a operação.'): MarketingError {
  if (error && typeof error === 'object' && (error as MarketingError).kind && typeof (error as MarketingError).status === 'number') {
    return error as MarketingError;
  }
  if (!(error instanceof ApiError)) {
    return {
      kind: 'network',
      status: 0,
      message: 'Sem resposta do servidor. Verifique a conexão e tente de novo.',
      blocks: [],
      code: null,
    };
  }
  const { message, code, blocks, envelope } = readBody(error.data);
  const base = { status: error.status, blocks, code };
  if (error.status === 403) {
    return {
      ...base,
      kind: code === 'not_contracted' ? 'not_contracted' : 'forbidden',
      message: message || 'Sem permissão para este módulo.',
    };
  }
  if (error.status === 404) {
    // A legacy-api responde HTML em rota desconhecida: sem envelope = rota não montada.
    if (!envelope) return { ...base, kind: 'unavailable', message: MODULE_UNAVAILABLE_MESSAGE };
    return { ...base, kind: 'not_found', message: message || 'Não encontrado.' };
  }
  if (error.status === 409) return { ...base, kind: 'conflict', message: message || 'O estado mudou. Atualize a página.' };
  if (error.status === 422) return { ...base, kind: 'rejected', message: message || fallback };
  if (error.status === 501 || error.status === 502 || error.status === 503 || error.status === 504) {
    return { ...base, kind: 'unavailable', message: message || MODULE_UNAVAILABLE_MESSAGE };
  }
  return { ...base, kind: 'unknown', message: message || error.message || fallback };
}

/** Mensagem pronta para o toast: a principal e, se houver, os bloqueios. */
export function marketingErrorToast(error: unknown, fallback?: string): { message: string; description?: string } {
  const mapped = toMarketingError(error, fallback);
  return {
    message: mapped.message,
    description: mapped.blocks.length ? mapped.blocks.join(' ') : undefined,
  };
}

export type ModuleGateKind = 'unavailable' | 'not_contracted' | 'forbidden';

export interface ModuleGate {
  kind: ModuleGateKind;
  message: string;
}

/**
 * Falha na LISTA do canal que fecha o módulo inteiro (em vez de cada página
 * mostrar o próprio erro). Erro de rede ou 500 fica na página, com "tentar de novo".
 */
export function moduleGateFor(error: MarketingError | null): ModuleGate | null {
  if (!error) return null;
  if (error.kind === 'unavailable' || error.kind === 'not_found') return { kind: 'unavailable', message: error.message };
  if (error.kind === 'not_contracted') return { kind: 'not_contracted', message: error.message };
  if (error.kind === 'forbidden') return { kind: 'forbidden', message: error.message };
  return null;
}

/** Erro que vale a pena tentar de novo sozinho (uma vez): só queda de rede. */
export function shouldRetryMarketing(failureCount: number, error: unknown): boolean {
  return failureCount < 1 && toMarketingError(error).kind === 'network';
}
